import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { TitleSection } from '../../components/title-section/title-section';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { ReportCategory } from "../../components/report-category/report-category";
import { IReportCategory, ISanctionsOptions, IStateOptions, SanctionCode, SanctionType, SubDialogValue } from './reports.interface';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { IconSvg } from "../../components/icon-svg/icon-svg";
import { TagModule } from 'primeng/tag';
import { ReportsService } from '../../services/reports.service';
import { ApplyContentRemovalSanction, ApplyPermanentSuspensionSanction, ApplyTemporarySuspensionSanction, ApplyWarningSanction, IReports } from '../../dto/reports';
import { ToastService } from '../../services/toast.service';
import { EMPTY, forkJoin, Subscription } from 'rxjs';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';
import { DatePipe } from '@angular/common';
import { mapPageNameToString, mapReportStatusToSeverity, mapReportStatusToString } from '../../utils/enum';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SANCTION_CODE_TO_TYPE, SANCTION_SEVERITY, sanctionsOptions, SeverityType, stateOptions } from './data/reports.data';
import { FormsModule } from '@angular/forms';
import { ACCOUNT_STATUS, REPORT_STATUS, SANCTION_TYPE } from '../../enum/report-status';
import { TextareaModule } from 'primeng/textarea';
import { isValueExist } from '../../utils/string';
import { RealtimeService } from '../../services/realtime';

@Component({
  selector: 'app-reports',
  imports: [
    TitleSection,
    ProgressSpinnerModule,
    DividerModule,
    TableModule,
    ReportCategory,
    SelectModule,
    MultiSelectModule,
    IconSvg,
    TagModule,
    CapitalizePipe,
    DatePipe,
    DialogModule,
    ButtonModule,
    SelectButtonModule,
    FormsModule,
    TextareaModule
  ],
  templateUrl: './reports.html',
  styleUrl: './reports.css',
})
export class Reports implements OnInit, OnDestroy {
  private reportsService = inject(ReportsService);
  private toastService = inject(ToastService);
  private realtimeService = inject(RealtimeService);

  private realtimeSubscription?: Subscription;

  isLoading = signal(false);
  isDialogVisible = signal(false);
  isSanctionLoading = signal(false);

  reports = signal<IReports[]>([]);
  selectedReport = signal<IReports | null>(null);
  categories = signal<IReportCategory[]>([]);

  stateOptions: IStateOptions[] = stateOptions;
  subModalValue: SubDialogValue = "details";
  sanctionsOptions: ISanctionsOptions[] = sanctionsOptions;
  sanctionsCode: SanctionCode = "disclaimer";

  textAreaValue = signal('');

  lastSanctionMap = signal<Map<string, SanctionType | null>>(new Map());
  SANCTION_COOLDOWN_DAYS = 7;

  mapPageNameToString = mapPageNameToString;
  mapReportStatusToString = mapReportStatusToString;
  mapReportStatusToSeverity = mapReportStatusToSeverity;

  get computedSanctionsOptions(): ISanctionsOptions[] {
    const hasContentId = !!this.selectedReport()?.contentId;
    const lastSanction = this.lastSanctionMap().get(this.selectedReport()?.reportedUser.userId ?? '') ?? null;

    return this.sanctionsOptions.map(option => ({
      ...option,
      disabled:
        (option.code === 'content-removal' && !hasContentId) ||
        (!!lastSanction && SANCTION_SEVERITY[SANCTION_CODE_TO_TYPE[option.code]] <= SANCTION_SEVERITY[lastSanction])
    }));
  }

  protected asReport(row: unknown): IReports {
    return row as IReports;
  }

  ngOnInit(): void {
    this.isLoading.set(true);
    this.realtimeService.subscribeToReports();

    this.realtimeSubscription = this.realtimeService.reportUpdated.subscribe(({ id, status }) => {
      this.reports.update(reports =>
        reports.map(r => r.id === id ? { ...r, status: status as REPORT_STATUS } : r)
      );
    });

    forkJoin({
      categories: this.reportsService.getReportCategories(),
      reports: this.reportsService.getReports()
    }).subscribe({
      next: ({ categories, reports }) => {
        this.categories.set(categories);
        this.reports.set(reports);
      },
      error: () => {
        this.toastService.error("Erreur lors du chargement des données");
        this.isLoading.set(false);
      },
      complete: () => {
        this.isLoading.set(false);
      }
    });
  }

  openDialog(report: IReports): void {
    this.selectedReport.set(report);
    this.isDialogVisible.set(true);
    this.subModalValue = "details";

    this.reportsService.checkCooldown(report.reportedUser.userId).subscribe(lastSanctionType => {
      this.lastSanctionMap.update(map =>
        new Map(map).set(report.reportedUser.userId, lastSanctionType)
      );
      this.sanctionsCode = this.getNextSanctionCode(lastSanctionType);
    });
  }

  private getNextSanctionCode(lastSanctionType: SanctionType | null): SanctionCode {
    const hasContentId = !!this.selectedReport()?.contentId;

    switch (lastSanctionType) {
      case 'WARNING': return hasContentId ? 'content-removal' : 'temporary-suspension';
      case 'CONTENT_REMOVAL': return 'temporary-suspension';
      case 'SUSPENSION': return 'permanent-suspension';
      case 'BAN': return 'permanent-suspension';
      default: return 'disclaimer';
    }
  }

  getTagValueFromAccountStatus(accountSatus: ACCOUNT_STATUS): string {
    switch (accountSatus) {
      case ACCOUNT_STATUS.ACTIVE:
        return "Active";
      case ACCOUNT_STATUS.SUSPENDED:
        return "Suspendu";
      case ACCOUNT_STATUS.BANNED:
        return "Banni";
      default:
        return "Inactif";
    }
  }

  getSeverityTagFromAccountStatus(accountSatus: ACCOUNT_STATUS): SeverityType {
    switch (accountSatus) {
      case ACCOUNT_STATUS.ACTIVE:
        return "success";
      case ACCOUNT_STATUS.SUSPENDED:
        return "warn";
      case ACCOUNT_STATUS.BANNED:
        return "danger";
      default:
        return "info";
    }
  }

  rejectReport() {
    this.toastService.info("La sanction a été rejetée");
    this.isDialogVisible.set(false);
  }

  applySanction(report: IReports): void {
    if (report.status === REPORT_STATUS.RESOLVED) return;
    if (this.isSanctionBlockedByCooldown(report.reportedUser.userId)) {
      this.toastService.warn(`Une sanction de niveau supérieur ou égal a déjà été appliquée dans les ${this.SANCTION_COOLDOWN_DAYS} derniers jours.`);
      return;
    }

    this.isSanctionLoading.set(true);

    const sanction$ = (() => {
      if (this.sanctionsCode === 'disclaimer') return this.disclaimerSanction(report);
      if (this.sanctionsCode === 'content-removal' && isValueExist(report.contentId)) return this.contentRemovalSanction(report);
      if (this.sanctionsCode === 'temporary-suspension') return this.temporarySuspensionSanction(report);
      if (this.sanctionsCode === 'permanent-suspension') return this.permanentSuspensionSanction(report);
      return EMPTY;
    })();

    sanction$.subscribe({
      next: () => {
        this.toastService.success("La sanction a été appliquée.");
        this.isDialogVisible.set(false);
      },
      error: (_err) => {
        this.toastService.error("Erreur lors de l'application de la sanction.", _err.message);
        this.isSanctionLoading.set(false);
      },
      complete: () => {
        this.isSanctionLoading.set(false);
      }
    });
  }

  disclaimerSanction(report: IReports) {
    const payload: ApplyWarningSanction = {
      reportId: report.id,
      sanctionedUserId: report.reportedUser.userId,
      reason: this.textAreaValue(),
      categoryId: report.category.id,
      type: SANCTION_TYPE.WARNING,
    };
    return this.reportsService.applyWarningSanction(payload);
  }

  contentRemovalSanction(report: IReports) {
    const payload: ApplyContentRemovalSanction = {
      reportId: report.id,
      sanctionedUserId: report.reportedUser.userId,
      reason: this.textAreaValue(),
      categoryId: report.category.id,
      contentId: report.contentId!,
      page: report.page,
      type: SANCTION_TYPE.CONTENT_REMOVAL
    };
    return this.reportsService.applyContentRemovalSanction(payload);
  }

  temporarySuspensionSanction(report: IReports) {
    const payload: ApplyTemporarySuspensionSanction = {
      reportId: report.id,
      sanctionedUserId: report.reportedUser.userId,
      sanctionedUserDisplayName: report.reportedUser.displayName,
      reason: this.textAreaValue(),
      categoryId: report.category.id,
      categoryTitle: report.category.title,
      type: SANCTION_TYPE.SUSPENSION
    };
    return this.reportsService.applyTemporarySuspensionSanction(payload);
  }

  permanentSuspensionSanction(report: IReports) {
    const payload: ApplyPermanentSuspensionSanction = {
      reportId: report.id,
      sanctionedUserId: report.reportedUser.userId,
      sanctionedUserDisplayName: report.reportedUser.displayName,
      reason: this.textAreaValue(),
      categoryId: report.category.id,
      categoryTitle: report.category.title,
      type: SANCTION_TYPE.BAN
    };
    return this.reportsService.applyPermanentSuspensionSanction(payload);
  }

  private isSanctionBlockedByCooldown(userId: string): boolean {
    const lastSanction = this.lastSanctionMap().get(userId) ?? null;
    if (!lastSanction) return false;
    return SANCTION_SEVERITY[SANCTION_CODE_TO_TYPE[this.sanctionsCode]] <= SANCTION_SEVERITY[lastSanction];
  }

  isBlockedByCooldown(report: IReports): boolean {
    return this.isSanctionBlockedByCooldown(report.reportedUser.userId);
  }

  isResolved(report: IReports): boolean {
    return report.status === REPORT_STATUS.RESOLVED;
  }

  ngOnDestroy(): void {
    this.realtimeService.unsubscribe();
    this.realtimeSubscription?.unsubscribe();
  }
}